'use client';
import Box from '@mui/material/Box';
import {styled} from '@mui/material/styles';
import {useTranslation} from 'react-i18next';
import {trackFooterSocialMediaClick} from '../../analytics';
import {
  FaFacebookBrandIcon,
  FaInstagramBrandIcon,
  FaLinkedinBrandIcon,
  FaTiktokBrandIcon,
  FaXBrandIcon,
  FaYoutubeBrandIcon,
  type IconDefinition,
} from '../icon';
import {SocialIconLink} from '../primitives';
import {ColumnHeading} from './styles';

interface FooterSocialColumnProps {
  social: {platform: string; href: string}[];
}

const SOCIAL_ICONS: Record<string, IconDefinition | undefined> = {
  instagram: FaInstagramBrandIcon,
  linkedin: FaLinkedinBrandIcon,
  x: FaXBrandIcon,
  youtube: FaYoutubeBrandIcon,
  facebook: FaFacebookBrandIcon,
  tiktok: FaTiktokBrandIcon,
};

const SOCIAL_LABELS: Record<string, string> = {
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  x: 'X',
  youtube: 'YouTube',
  facebook: 'Facebook',
  tiktok: 'TikTok',
};

// The analytics event only models facebook / instagram / linkedin; other
// platforms render but are not tracked (matching the implemented event type).
const TRACKED_SOCIALS = ['facebook', 'instagram', 'linkedin'] as const;
type TrackedSocial = (typeof TRACKED_SOCIALS)[number];
const isTrackedSocial = (platform: string): platform is TrackedSocial => (TRACKED_SOCIALS as readonly string[]).includes(platform);

const SocialRow = styled(Box)(({theme}) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
}));

export function FooterSocialColumn({social}: FooterSocialColumnProps) {
  const {t} = useTranslation();
  return (
    <Box>
      <ColumnHeading>{t('follow_us')}</ColumnHeading>
      <SocialRow>
        {social.map(socialLink => {
          const platformIcon = SOCIAL_ICONS[socialLink.platform];
          // Skip platforms with no mapped icon rather than rendering an empty circle.
          if (!platformIcon) {
            return null;
          }
          const tracked = isTrackedSocial(socialLink.platform) ? socialLink.platform : undefined;
          return (
            <SocialIconLink
              key={socialLink.platform}
              href={socialLink.href}
              icon={platformIcon}
              label={SOCIAL_LABELS[socialLink.platform] ?? socialLink.platform}
              onClick={
                tracked
                  ? () => {
                      trackFooterSocialMediaClick(tracked);
                    }
                  : undefined
              }
            />
          );
        })}
      </SocialRow>
    </Box>
  );
}

FooterSocialColumn.displayName = 'FooterSocialColumn';
